import {
	type Group,
	DATES_GROUP,
	LABELS_GROUP,
	NUMBER_GROUP,
	OTHER_GROUP,
	PEOPLE_GROUP,
	SELECT_GROUP,
	SYSTEM_GROUP,
	TEXT_GROUP,
	ADVANCED_GROUP,
	SUGGESTED_GROUP,
	RESTRICTED_GROUP,
} from '../../constants.tsx';
import type { AvailableLayoutItemGroup } from '../../types/layout-items.tsx';
import type { CustomAvailableLayoutItemsGroup } from '../fragments/__generated_apollo__/CustomAvailableLayoutItemsGroup';
import type { SystemAvailableLayoutItemsGroup } from '../fragments/__generated_apollo__/SystemAvailableLayoutItemsGroup';
import { transformBaseLayoutItems } from './base-layout-items.tsx';
import platformTransformers from './platform-transformers.tsx';

type GQLGroup = SystemAvailableLayoutItemsGroup | CustomAvailableLayoutItemsGroup;

export const transformAvailableLayoutItemsGroup = (
	groupId: Group,
	{ items: { pageInfo, totalCount, edges } }: GQLGroup,
): AvailableLayoutItemGroup => {
	const items = transformBaseLayoutItems(
		edges.map(({ node }) => node),
		platformTransformers,
	);

	const hasNextPage = pageInfo?.hasNextPage || false;
	const endCursor = pageInfo?.endCursor || null;

	return {
		groupId,
		totalCount: totalCount || 0,
		hasNextPage,
		endCursor,
		items,
	};
};

export const transformAvailableLayoutItemGroups = (gqlGroups: {
	readonly __typename?: string;
	readonly systemAndAppFields?: GQLGroup;
	readonly textFields?: GQLGroup;
	readonly labelsFields?: GQLGroup;
	readonly peopleFields?: GQLGroup;
	readonly dateFields?: GQLGroup;
	readonly selectFields?: GQLGroup;
	readonly numberFields?: GQLGroup;
	readonly advancedFields?: GQLGroup;
	readonly otherFields?: GQLGroup;
	readonly suggestedFields?: GQLGroup;
	readonly restrictedFields?: GQLGroup;
}): AvailableLayoutItemGroup[] => {
	const {
		systemAndAppFields,
		textFields,
		labelsFields,
		peopleFields,
		dateFields,
		selectFields,
		numberFields,
		otherFields,
		advancedFields,
		suggestedFields,
		restrictedFields,
	} = gqlGroups;

	const result: Array<AvailableLayoutItemGroup> = [];

	if (systemAndAppFields) {
		result.push(transformAvailableLayoutItemsGroup(SYSTEM_GROUP, systemAndAppFields));
	}

	if (textFields) {
		result.push(transformAvailableLayoutItemsGroup(TEXT_GROUP, textFields));
	}

	if (suggestedFields) {
		result.push(transformAvailableLayoutItemsGroup(SUGGESTED_GROUP, suggestedFields));
	}

	if (dateFields) {
		result.push(transformAvailableLayoutItemsGroup(DATES_GROUP, dateFields));
	}

	if (labelsFields) {
		result.push(transformAvailableLayoutItemsGroup(LABELS_GROUP, labelsFields));
	}

	if (numberFields) {
		result.push(transformAvailableLayoutItemsGroup(NUMBER_GROUP, numberFields));
	}

	if (peopleFields) {
		result.push(transformAvailableLayoutItemsGroup(PEOPLE_GROUP, peopleFields));
	}

	if (selectFields) {
		result.push(transformAvailableLayoutItemsGroup(SELECT_GROUP, selectFields));
	}

	if (otherFields) {
		result.push(transformAvailableLayoutItemsGroup(OTHER_GROUP, otherFields));
	}
	if (advancedFields) {
		result.push(transformAvailableLayoutItemsGroup(ADVANCED_GROUP, advancedFields));
	}
	if (restrictedFields) {
		result.push(transformAvailableLayoutItemsGroup(RESTRICTED_GROUP, restrictedFields));
	}

	return result;
};
